
import { supabase } from '@/integrations/supabase/client';
import { Lead } from '@/types/lead';
import { WebSearchData, WebSearchStatus } from '@/types/webSearch';

export interface WebSearchResult {
  summary: string;
  timestamp: string;
  sources: Array<{
    url: string;
    title: string;
    indexRange?: string;
  }>;
  services?: string[];
  keyPeople?: string[];
  companyDetails?: {
    foundedYear?: number;
    [key: string]: any;
  };
  reviews?: {
    rating?: number;
    count?: number;
    [key: string]: any;
  };
  rawText?: string;
  [key: string]: any;
}

export const enrichLeadWithWebSearch = async (lead: Lead, forceReenrich: boolean = false): Promise<WebSearchData | null> => {
  try {
    console.log(`Requesting web search enrichment for lead: ${lead.id}`);
    
    const updateObj: any = {
      web_search_status: 'pending',
      web_search_queue_time: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const { error: updateError } = await supabase
      .from('leads')
      .update(updateObj)
      .eq('id', lead.id);

    if (updateError) {
      console.error('Error updating lead status:', updateError);
      throw updateError;
    }
    
    const { data, error } = await supabase.functions.invoke('enrich-lead', {
      body: { 
        leadId: lead.id,
        leadData: {
          name: lead.name,
          address: lead.address,
          category: lead.category,
          website: lead.website,
          placeId: lead.placeId
        },
        forceReenrich
      }
    });

    if (error) {
      console.error('Error enriching lead with web search:', error);
    }

    return data?.data || null;
  } catch (error) {
    console.error('Error in enrichLeadWithWebSearch:', error);
    return null;
  }
};

export const getWebSearchData = async (leadId: string): Promise<WebSearchData | null> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('web_search_data, web_search_updated_at, web_search_status')
      .eq('id', leadId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching web search data:', error);
      return null;
    }

    if (!data || !data.web_search_data) {
      return null;
    }

    const webSearchData = data.web_search_data as any;
    if (webSearchData?.linkedIn?.dataSource === 'verified_linkedin' && data.web_search_status !== 'completed') {
      await supabase
        .from('leads')
        .update({
          web_search_status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);
    }

    // Normalize legacy "linkedin" property to "linkedIn"
    const result = data.web_search_data as any;
    if (!result.linkedIn && result.linkedin) {
      result.linkedIn = result.linkedin;
      delete result.linkedin;
    }

    // Sanitize LinkedIn fields to remove placeholders
    const PLACEHOLDERS = [
      'not found',
      'not available',
      'not specified',
      'not mentioned',
      'not explicitly mentioned',
      'are not explicitly mentioned',
      'not provided',
      'unknown',
      'n/a',
      'none found',
      'no information',
      'information not available',
      'information not accessible',
      'not accessible',
      'requires login',
      'login required',
      'requires sign in',
      'sign in',
      'sign-in required',
      'members only',
      'private',
      'restricted',
      'access denied',
      'publicly unavailable',
      'not publicly available',
      'no public information',
      'cannot access',
      'unable to access',
      'content unavailable'
    ];
    
    const isPlaceholder = (v?: string | null) => {
      if (!v) return true;
      const val = v.toLowerCase().trim();
      return !val || PLACEHOLDERS.some(p => val === p || val.includes(p));
    };
    
    const sanitizeVal = (v?: string | null) => isPlaceholder(v) ? null : v!.trim();

    if (result.linkedIn) {
      const li = result.linkedIn;
      li.companyDescription = sanitizeVal(li.companyDescription);
      li.website = sanitizeVal(li.website);
      li.employeeCount = sanitizeVal(li.employeeCount);
      li.industry = sanitizeVal(li.industry);
      li.headquarters = sanitizeVal(li.headquarters);
      if (Array.isArray(li.specialties)) {
        li.specialties = li.specialties
          .map(sanitizeVal)
          .filter(Boolean);
      }
    }

    return result as WebSearchData;
  } catch (error) {
    console.error('Error in getWebSearchData:', error);
    return null;
  }
};

export const getWebSearchStatus = async (leadId: string): Promise<{
  status: WebSearchStatus;
  updatedAt: Date | null;
  queuedAt: Date | null;
  queuePosition: number | null;
} | null> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('web_search_status, web_search_updated_at, web_search_queue_time, web_search_data')
      .eq('id', leadId)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    const webSearchData = data.web_search_data as any;
    const liSource = webSearchData?.linkedIn?.dataSource;
    const hasData = webSearchData && (webSearchData.summary || webSearchData.places?.placeId);
    
    // Map status to 'completed' if we have useful data, even with verification_needed/not_found
    let effectiveStatus: WebSearchStatus = (data.web_search_status as WebSearchStatus) || 'pending';
    if (liSource === 'verified_linkedin' || (hasData && (effectiveStatus === 'verification_needed' || effectiveStatus === 'not_found'))) {
      effectiveStatus = 'completed';
    }

    // Calculate real queue position if pending
    let queuePosition = null;
    if (effectiveStatus === 'pending' && data.web_search_queue_time) {
      const { count } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('web_search_status', 'pending')
        .lt('web_search_queue_time', data.web_search_queue_time);

      queuePosition = (count || 0) + 1;
    }

    return {
      status: effectiveStatus,
      updatedAt: data.web_search_updated_at ? new Date(data.web_search_updated_at) : null,
      queuedAt: data.web_search_queue_time ? new Date(data.web_search_queue_time) : null,
      queuePosition,
    };
  } catch (error) {
    console.error('Error in getWebSearchStatus:', error);
    return null;
  }
};

export const getWebSearchUpdateTime = async (leadId: string): Promise<Date | null> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('web_search_updated_at')
      .eq('id', leadId)
      .maybeSingle();

    if (error || !data || !data.web_search_updated_at) {
      return null;
    }

    return new Date(data.web_search_updated_at);
  } catch (error) {
    console.error('Error in getWebSearchUpdateTime:', error);
    return null;
  }
};

export const processWebSearchQueue = async (
  options: { batchSize?: number; prioritizeLeadId?: string } = {}
): Promise<{
  processed: number;
  results: Array<{
    leadId: string;
    status: WebSearchStatus;
    skipped?: boolean;
    error?: string;
  }>;
}> => {
  try {
    const { batchSize = 3, prioritizeLeadId } = options;
    const results: Array<{
      leadId: string;
      status: WebSearchStatus;
      skipped?: boolean;
      error?: string;
    }> = [];

    // If a specific lead is prioritized, process it immediately
    if (prioritizeLeadId) {
      const { data: targetLead, error: targetError } = await supabase
        .from('leads')
        .select('id, name, address, category, website, web_search_status')
        .eq('id', prioritizeLeadId)
        .maybeSingle();

      if (!targetError && targetLead && targetLead.web_search_status !== 'completed') {
        // Update status to processing
        await supabase
          .from('leads')
          .update({ 
            web_search_status: 'processing',
            web_search_updated_at: new Date().toISOString()
          })
          .eq('id', targetLead.id);

        try {
          const { error: enrichError } = await supabase.functions.invoke('enrich-lead', {
            body: {
              leadId: targetLead.id,
              leadData: {
                name: targetLead.name,
                address: targetLead.address,
                category: targetLead.category,
                website: targetLead.website
              }
            }
          });

          if (enrichError) {
            await supabase
              .from('leads')
              .update({ 
                web_search_status: 'failed',
                web_search_updated_at: new Date().toISOString()
              })
              .eq('id', targetLead.id);
            
            results.push({ 
              leadId: targetLead.id, 
              status: 'failed', 
              error: enrichError.message 
            });
          } else {
            results.push({ leadId: targetLead.id, status: 'completed' });
          }
        } catch (err) {
          await supabase
            .from('leads')
            .update({ 
              web_search_status: 'failed',
              web_search_updated_at: new Date().toISOString()
            })
            .eq('id', targetLead.id);
          
          results.push({ 
            leadId: targetLead.id, 
            status: 'failed', 
            error: err instanceof Error ? err.message : 'Unknown error' 
          });
        }
      } else if (targetLead?.web_search_status === 'completed') {
        results.push({ leadId: targetLead.id, status: 'completed', skipped: true });
      }
    }
    console.log('Processing web search queue');
    
    // Fetch pending leads from the queue (excluding the prioritized one if already processed)
    let query = supabase
      .from('leads')
      .select('id, name, address, category, website')
      .eq('web_search_status', 'pending')
      .order('web_search_queue_time', { ascending: true })
      .limit(batchSize);
    
    // Exclude the prioritized lead if it was already processed
    if (prioritizeLeadId && results.length > 0) {
      query = query.neq('id', prioritizeLeadId);
    }

    const { data: pendingLeads, error: fetchError } = await query;
    
    if (fetchError) {
      console.error('Error fetching pending leads:', fetchError);
      throw fetchError;
    }
    
    if (!pendingLeads || pendingLeads.length === 0) {
      console.log('No pending leads in the queue');
      return { processed: results.length, results };
    }
    
    console.log(`Processing ${pendingLeads.length} leads`);
    
    for (const lead of pendingLeads) {
      try {
        const existingData = await getWebSearchData(lead.id);
        if (existingData) {
          await supabase
            .from('leads')
            .update({
              web_search_status: 'completed',
              updated_at: new Date().toISOString()
            })
            .eq('id', lead.id);
          
          results.push({ leadId: lead.id, status: 'completed', skipped: true });
          continue;
        }
        
        await supabase
          .from('leads')
          .update({
            web_search_status: 'processing',
            updated_at: new Date().toISOString()
          })
          .eq('id', lead.id);
        
        const { data, error } = await supabase.functions.invoke('enrich-lead', {
          body: { 
            leadId: lead.id,
            leadData: {
              name: lead.name,
              address: lead.address,
              category: lead.category,
              website: lead.website
            }
          }
        });
        
        if (error) {
          console.error(`Error processing lead ${lead.id}:`, error);
          
          await supabase
            .from('leads')
            .update({
              web_search_status: 'failed',
              updated_at: new Date().toISOString()
            })
            .eq('id', lead.id);
          
          results.push({ leadId: lead.id, status: 'failed', error: error.message });
        } else {
          results.push({ leadId: lead.id, status: 'completed' });
        }
      } catch (error) {
        console.error(`Error processing lead ${lead.id}:`, error);
        
        await supabase
          .from('leads')
          .update({
            web_search_status: 'failed',
            updated_at: new Date().toISOString()
          })
          .eq('id', lead.id);
        
        results.push({ 
          leadId: lead.id, 
          status: 'failed', 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    }
    
    return { processed: results.length, results };
  } catch (error) {
    console.error('Error in processWebSearchQueue:', error);
    throw error;
  }
};

export const verifyLinkedInForLead = async (leadId: string, manualLinkedInUrl: string): Promise<WebSearchData | null> => {
  try {
    console.log(`Verifying LinkedIn URL for lead ${leadId}: ${manualLinkedInUrl}`);
    
    const { data, error } = await supabase.functions.invoke('enrich-linkedin', {
      body: {
        leadId,
        linkedInUrl: manualLinkedInUrl
      }
    });

    if (error) {
      console.error('Error verifying LinkedIn for lead:', error);
      throw error;
    }

    return data?.linkedIn || null;
  } catch (error) {
    console.error('Error in verifyLinkedInForLead:', error);
    throw error;
  }
};
